"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";

/**
 * 홈 — 📰 사장님 일상 미니뷰.
 *
 * 2026-05-14 v2 — 사용자 결정:
 *   - 뉴스 카드는 제목만 표시 (요약 제거, 단순화)
 *   - 식자재는 핵심 4 품목 (망고·바나나·딸기·계란) 가로 그리드 strip
 *
 * 비로그인 락 오버레이 유지.
 */

interface FeedItem {
  kind: "news" | "ingredient";
  category: string;
  category_label: string;
  // news
  news_id?: number | null;
  title?: string | null;
  summary?: string | null;
  url?: string | null;
  source?: string | null;
  published_at?: string | null;
  // ingredient
  item_name?: string | null;
  price?: number | null;
  unit?: string | null;
  region?: string | null;
  change_vs_lastweek_pct?: number | null;
}

interface FeedOut {
  items: FeedItem[];
}

// 정부지원금 (마이그 073, 2026-05-15)
interface SubventionMiniRow {
  id: number;
  title: string;
  region: string | null;
  days_left: number | null;
  apply_end: string | null;
  support_type: string | null;
}
interface SubventionMiniOut {
  items: SubventionMiniRow[];
  total_curated: number;
}

// 식자재 4 품목 — 카페에서 가장 자주 쓰이는 핵심 (사용자 결정 2026-05-14 → 5/20 우유→딸기 교체)
const HOME_INGREDIENT_ITEMS = ["mango", "banana", "strawberry", "egg"];

interface IngredientPoint {
  item_code: string;
  item_name: string;
  unit: string | null;
  region: string;
  price: number;
  change_vs_lastweek_pct: number | null;
}

interface IngredientSummaryOut {
  items: IngredientPoint[];
  as_of: string | null;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "";
  const d = new Date(s);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function fmtNumber(n: number): string {
  return n.toLocaleString();
}

function trackClick(newsId: number | null | undefined) {
  if (!newsId) return;
  axios.post(`/api/news/${newsId}/click`).catch(() => {});
}

export function DailyNewsMiniSection() {
  const { isLoggedIn, token } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const [items, setItems] = useState<FeedItem[]>([]);
  const [ingredients, setIngredients] = useState<IngredientPoint[]>([]);
  const [subventions, setSubventions] = useState<SubventionMiniRow[]>([]);
  const [subventionTotal, setSubventionTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      axios.get<FeedOut>("/api/news/feed", { params: { per_category: 1 } }),
      axios.get<IngredientSummaryOut>("/api/news/ingredient-summary", { params: { region: "가락" } }),
      // 정부지원금 (마이그 073) — 매장 매칭 (token 있으면 자동)
      axios.get<SubventionMiniOut>("/api/subventions/home-mini", { params: { n: 3 }, headers }),
    ])
      .then(([f, ing, sv]) => {
        setItems((f.data.items || []).filter((it) => it.kind === "news"));
        const byCode = new Map(ing.data.items.map((it) => [it.item_code, it]));
        const picked = HOME_INGREDIENT_ITEMS.map((c) => byCode.get(c)).filter((x): x is IngredientPoint => !!x);
        setIngredients(picked);
        setSubventions(sv.data.items || []);
        setSubventionTotal(sv.data.total_curated || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  // 셋 다 0건이면 섹션 숨김
  if (!loading && items.length === 0 && ingredients.length === 0 && subventionTotal === 0) return null;
  const showBlur = hasHydrated && !isLoggedIn;

  return (
    <section className="px-4 mt-6 min-h-[280px]">
      <div className="flex items-center justify-between mb-3">
        <h2 className="section-title-major mb-0">📰 사장님 일상</h2>
        {!showBlur && (
          <Link href="/news" className="text-xs text-primary font-semibold">
            더보기 →
          </Link>
        )}
      </div>

      <div className="rounded-2xl border-2 border-border bg-bg-card/40 px-3 py-3 space-y-3">
        {loading ? (
          <p className="text-2xs text-text-sub py-4 text-center">불러오는 중...</p>
        ) : (
          <>
            {/* 식자재 strip — 비로그인 시에도 노출 (단순 가격 정보, 사용자 결정 2026-05-15) */}
            {ingredients.length > 0 && <IngredientStrip items={ingredients} />}

            {/* 정부지원금 — 데이터 취합 중 안내 (사용자 결정 2026-05-15) */}
            <SubventionStrip items={subventions} total={subventionTotal} showBlur={showBlur} />


            {/* 뉴스 — 비로그인 시 첫 1개 노출 + 나머지 부분 블러 (사용자 결정 2026-05-15) */}
            {items.length > 0 && (
              <div className="space-y-1.5">
                {items.map((it, i) => {
                  const shouldBlur = showBlur && i > 0;
                  return (
                    <div key={`${it.category}-${i}`} className={shouldBlur ? "blur-sm select-none pointer-events-none" : ""}>
                      <FeedCard item={it} />
                    </div>
                  );
                })}
              </div>
            )}

            {showBlur && (
              <Link
                href="/login"
                className="block text-center text-2xs font-bold text-primary border border-primary/30 rounded-md py-2 hover:bg-primary/5"
              >
                🔒 로그인하면 매일 자동 업데이트 받기 →
              </Link>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function IngredientStrip({ items }: { items: IngredientPoint[] }) {
  return (
    <Link
      href="/news?tab=ingredient_price"
      className="block mb-2 rounded-lg bg-bg-elevated/40 px-2 py-2 hover:bg-bg-elevated transition-colors"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-2xs text-primary font-bold">식자재 도매가</span>
        <span className="text-2xs text-text-sub">· 가락시장</span>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {items.map((it) => {
          const ch = it.change_vs_lastweek_pct;
          const chColor =
            ch === null || ch === undefined
              ? "text-text-sub"
              : ch > 0
                ? "text-red-400"
                : ch < 0
                  ? "text-emerald-500"
                  : "text-text-sub";
          const chText =
            ch === null || ch === undefined
              ? "—"
              : ch > 0
                ? `↑${ch.toFixed(0)}%`
                : ch < 0
                  ? `↓${Math.abs(ch).toFixed(0)}%`
                  : "±0";
          return (
            <div key={it.item_code} className="flex flex-col items-center text-center bg-bg-card/60 rounded-md py-1.5 px-1">
              <span className="text-2xs font-semibold text-text leading-tight">
                {it.item_name}
                {it.unit && <span className="font-normal text-text-sub ml-0.5">{it.unit}</span>}
              </span>
              <span className="text-2xs font-bold text-text mt-1">{fmtNumber(it.price)}원</span>
              <span className={`text-2xs ${chColor}`}>{chText}</span>
            </div>
          );
        })}
      </div>
    </Link>
  );
}

function FeedCard({ item }: { item: FeedItem }) {
  // news 만 처리 (ingredient 는 IngredientStrip 으로 분리)
  return (
    <a
      href={item.url ?? "#"}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackClick(item.news_id)}
      className="block rounded-md bg-bg-elevated/50 px-2.5 py-1.5 hover:bg-bg-elevated transition-colors"
    >
      <div className="flex items-center gap-2">
        <span className="text-2xs text-primary font-semibold shrink-0">{item.category_label}</span>
        <p className="text-sm font-semibold text-text leading-snug line-clamp-1 flex-1">{item.title}</p>
        {item.published_at && (
          <span className="text-2xs text-text-sub shrink-0">{fmtDate(item.published_at)}</span>
        )}
      </div>
    </a>
  );
}

// 정부지원금 strip (마이그 073, 2026-05-15)
const SUPPORT_TYPE_LABEL: Record<string, string> = {
  fund: "자금", tax: "세제", facility: "시설", education: "교육",
  consulting: "컨설팅", other: "기타",
};

function SubventionStrip(_props: {
  items: SubventionMiniRow[];
  total: number;
  showBlur: boolean;
}) {
  // 2026-05-15 — 데이터 누적 부족으로 안내만 (사용자 결정)
  return (
    <div className="rounded-lg bg-bg-elevated/40 px-2.5 py-2.5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-2xs font-bold text-primary">정부지원금</span>
        <span className="text-2xs text-text-sub">데이터 취합 중</span>
      </div>
      <p className="text-2xs text-text-sub leading-snug">
        카페·외식업에 맞는 지원금을 모으고 있어요. 풍부해지면 매장에 자동 매칭됩니다.
      </p>
    </div>
  );
}
